import { useState, useEffect, useMemo } from "react";

export const CURRENT_YEAR = new Date().getFullYear();
export const CURRENT_WBR_WEEK = (() => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const days = Math.floor((now - start) / 86400000);
  return Math.ceil((days + start.getDay() + 1) / 7) - 1;
})();

/**
 * Manages week/year selection + derived dropdown lists.
 *
 * Two modes:
 *  - Self-managed (default): call useWeekYear(startYear) and it owns the
 *    selection state. Backwards compatible with old callers.
 *  - Externally-owned selection: pass a second arg
 *    { selectedWeek, selectedYear, setSelectedWeek, setSelectedYear }.
 *    Used by FunctionPage so it can drive the config fetch from a single source
 *    of truth (no more calling this hook twice).
 *
 * `startYear` ONLY shapes the year dropdown list; it never resets the current
 * selection, so it's safe to pass it late (after config loads).
 */
export function useWeekYear(startYear, external) {
  const internalWeek = useState(CURRENT_WBR_WEEK);
  const internalYear = useState(CURRENT_YEAR);

  const selectedWeek = external ? external.selectedWeek : internalWeek[0];
  const setSelectedWeek = external ? external.setSelectedWeek : internalWeek[1];
  const selectedYear = external ? external.selectedYear : internalYear[0];
  const setSelectedYear = external ? external.setSelectedYear : internalYear[1];

  const yearList = useMemo(() => {
    if (!startYear) return [];
    const years = [];
    for (let y = CURRENT_YEAR; y >= startYear; y--) years.push(y);
    return years;
  }, [startYear]);

  const weekList = useMemo(() => {
    const maxWeek = selectedYear === CURRENT_YEAR ? CURRENT_WBR_WEEK : 52;
    return Array.from({ length: maxWeek }, (_, i) => i + 1);
  }, [selectedYear]);

  // Clamp week if year changes and current week exceeds max
  useEffect(() => {
    const maxWeek = selectedYear === CURRENT_YEAR ? CURRENT_WBR_WEEK : 52;
    if (selectedWeek > maxWeek) setSelectedWeek(maxWeek);
  }, [selectedYear, selectedWeek, setSelectedWeek]);

  return {
    selectedWeek,
    selectedYear,
    setSelectedWeek,
    setSelectedYear,
    weekList,
    yearList,
    CURRENT_YEAR,
    CURRENT_WBR_WEEK,
  };
}
